import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Shield, ShieldCheck, Ban, Search, Flag, Trash2, ScrollText, Star, UserX, Unlock, RotateCcw, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const DURATIONS = [
  { key: "1d", label: "1 jour" },
  { key: "2d", label: "2 jours" },
  { key: "3d", label: "3 jours" },
  { key: "4d", label: "4 jours" },
  { key: "1m", label: "1 mois" },
  { key: "def", label: "Définitif" },
];

function banLabel(until) {
  if (!until) return null;
  if (until === "definitive") return "Banni définitivement";
  const d = new Date(until);
  if (d < new Date()) return null;
  return `Banni jusqu'au ${d.toLocaleDateString("fr-FR")} ${d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
}

export default function AdminModeration({ isAdmin = false }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState({});

  const load = () => { setLoading(true); api.get("/admin/users").then((r) => setUsers(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const toggleMod = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/moderator`, { value: !u.is_moderator });
      toast.success(!u.is_moderator ? `${u.prenom} est modérateur` : `${u.prenom} n'est plus modérateur`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleOfficial = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/official`, { value: !u.is_official });
      toast.success(!u.is_official ? `${u.prenom} rejoint la team A Nous Turf` : `${u.prenom} n'est plus membre officiel`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleFreeAccess = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/free-access`, { value: !u.free_access });
      toast.success(!u.free_access ? `Accès gratuit accordé à ${u.prenom}` : `Accès gratuit retiré à ${u.prenom}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteMember = async (u) => {
    if (!window.confirm(`Supprimer définitivement ${u.prenom} ${u.nom} ?\nCette action est irréversible et efface ses paris et messages.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Membre supprimé");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const ban = async (u, duration) => {
    let reason = "";
    if (duration !== "lift") {
      reason = window.prompt("Raison du bannissement (envoyée au joueur, optionnel) :", "") || "";
    }
    try {
      await api.post("/feed/ban", { user_id: u.id, duration, reason });
      toast.success(duration === "lift" ? "Bannissement levé" : `${u.prenom} banni du mur`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const [reports, setReports] = useState([]);
  const [logs, setLogs] = useState([]);
  const loadMod = () => {
    api.get("/admin/reports").then((r) => setReports(r.data)).catch(() => {});
    api.get("/admin/mod-log").then((r) => setLogs(r.data)).catch(() => {});
  };
  useEffect(() => { loadMod(); }, []);

  const delReported = async (postId) => {
    try { await api.delete(`/feed/${postId}`); toast.success("Message supprimé"); loadMod(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const [resetting, setResetting] = useState(false);
  const resetGame = async () => {
    if (!window.confirm("⚠️ Remettre à zéro « Le Jeu » ET le Classement ?\n\nCela supprime DÉFINITIVEMENT :\n• toutes les courses importées de Geny\n• tous les paris et points des joueurs\n• tous les classements et saisons\n\nCette action est IRRÉVERSIBLE.")) return;
    if (!window.confirm("Dernière confirmation : voulez-vous vraiment tout effacer ?")) return;
    setResetting(true);
    try {
      const r = await api.post("/admin/game/reset");
      toast.success(`Réinitialisé : ${r.data.courses_deleted} course(s) et ${r.data.predictions_deleted} pari(s) supprimés.`);
      loadMod();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setResetting(false); }
  };

  const filtered = users.filter((u) => `${u.prenom} ${u.nom} ${u.email}`.toLowerCase().includes(q.toLowerCase()));

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-[#00E676]" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 bg-[#0F1812] border border-[#00E676]/20 rounded-lg px-3 py-2 max-w-md">
        <Search size={16} className="text-[#697C6F]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un joueur…" data-testid="mod-search" className="bg-transparent text-sm w-full focus:outline-none" />
      </div>

      <div className="space-y-2">
        {filtered.map((u) => {
          const banned = banLabel(u.wall_banned_until);
          return (
            <div key={u.id} className="rounded-xl border border-[#00E676]/15 bg-[#131F17] p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`mod-user-${u.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-bold flex items-center gap-2 flex-wrap">
                  {u.prenom} {u.nom}
                  {u.role === "admin" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#39FF14] text-[#04180a] font-bold">ADMIN</span>}
                  {u.is_official && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FFD700]/20 text-[#FFD700] font-bold">MEMBRE OFFICIEL</span>}
                  {u.free_access && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#00E676]/20 text-[#39FF14] font-bold">ACCÈS GRATUIT</span>}
                  {u.is_moderator && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#00E676]/20 text-[#39FF14] font-bold">MODÉRATEUR</span>}
                  {banned && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FF3B30]/15 text-[#FF3B30] font-bold">{banned}</span>}
                </div>
                <div className="text-xs text-[#697C6F]">{u.email}</div>
              </div>

              {u.role !== "admin" && (
                <div className="flex items-center gap-2 flex-wrap">
                  {isAdmin && (
                    <button onClick={() => toggleOfficial(u)} data-testid={`toggle-official-${u.id}`}
                      className={`text-xs font-bold px-3 py-1.5 rounded-md flex items-center gap-1 ${u.is_official ? "bg-[#FFD700]/20 text-[#FFD700]" : "bg-[#131F17] text-[#A1B5A8] border border-[#FFD700]/30 hover:text-[#FFD700]"}`}>
                      <Star size={13} className={u.is_official ? "fill-[#FFD700]" : ""} /> {u.is_official ? "Retirer officiel" : "Membre officiel"}
                    </button>
                  )}
                  {isAdmin && (
                    <button onClick={() => toggleFreeAccess(u)} data-testid={`toggle-free-${u.id}`}
                      className={`text-xs font-bold px-3 py-1.5 rounded-md flex items-center gap-1 ${u.free_access ? "bg-[#00E676]/20 text-[#39FF14]" : "bg-[#131F17] text-[#A1B5A8] border border-[#00E676]/30 hover:text-[#39FF14]"}`}>
                      <Unlock size={13} /> {u.free_access ? "Retirer accès" : "Accès gratuit"}
                    </button>
                  )}
                  {isAdmin && (
                    <button onClick={() => toggleMod(u)} data-testid={`toggle-mod-${u.id}`}
                      className={`text-xs font-bold px-3 py-1.5 rounded-md flex items-center gap-1 ${u.is_moderator ? "bg-[#1A2B20] text-[#39FF14]" : "bg-[#00E676] text-[#04180a]"}`}>
                      {u.is_moderator ? <ShieldCheck size={13} /> : <Shield size={13} />} {u.is_moderator ? "Retirer modo" : "Modérateur"}
                    </button>
                  )}
                  <select value={sel[u.id] || "1d"} onChange={(e) => setSel({ ...sel, [u.id]: e.target.value })} data-testid={`ban-duration-${u.id}`}
                    className="bg-[#0F1812] border border-[#00E676]/20 rounded-md text-xs px-2 py-1.5 focus:outline-none">
                    {DURATIONS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
                  </select>
                  <button onClick={() => ban(u, sel[u.id] || "1d")} data-testid={`ban-${u.id}`} className="text-xs font-bold px-3 py-1.5 rounded-md bg-[#FF3B30]/15 text-[#FF3B30] flex items-center gap-1">
                    <Ban size={13} /> Bannir
                  </button>
                  {banned && (
                    <button onClick={() => ban(u, "lift")} data-testid={`unban-${u.id}`} className="text-xs font-bold px-3 py-1.5 rounded-md bg-[#1A2B20] text-[#A1B5A8] hover:text-white">Lever</button>
                  )}
                  {isAdmin && (
                    <button onClick={() => deleteMember(u)} data-testid={`delete-user-${u.id}`} className="text-xs font-bold px-3 py-1.5 rounded-md bg-[#FF3B30] text-white flex items-center gap-1">
                      <UserX size={13} /> Supprimer
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-6">
        <div className="rounded-xl border border-[#00E676]/15 bg-[#131F17] p-4">
          <h3 className="font-bold flex items-center gap-2 mb-3"><Flag size={16} className="text-[#FFCC00]" /> Signalements ({reports.length})</h3>
          {reports.length === 0 ? <p className="text-sm text-[#697C6F]">Aucun signalement.</p> : (
            <div className="space-y-2" data-testid="reports-list">
              {reports.map((r) => (
                <div key={r.id} className="rounded-lg border border-[#00E676]/10 bg-[#0F1812] p-3 text-sm">
                  <div className="text-[#A1B5A8]">Message de <span className="font-semibold text-white">{r.post_author}</span> : « {r.post_excerpt} »</div>
                  <div className="text-xs text-[#697C6F] mt-1">Signalé par {r.reporter_name}{r.reason ? ` — ${r.reason}` : ""}</div>
                  <button onClick={() => delReported(r.post_id)} data-testid={`del-reported-${r.post_id}`} className="mt-2 text-xs font-bold px-3 py-1.5 rounded-md bg-[#FF3B30]/15 text-[#FF3B30] flex items-center gap-1"><Trash2 size={12} /> Supprimer le message</button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-[#00E676]/15 bg-[#131F17] p-4">
          <h3 className="font-bold flex items-center gap-2 mb-3"><ScrollText size={16} className="text-[#00E676]" /> Journal de modération</h3>
          {logs.length === 0 ? <p className="text-sm text-[#697C6F]">Aucune action enregistrée.</p> : (
            <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="modlog-list">
              {logs.map((l) => (
                <div key={l.id} className="text-sm border-b border-[#00E676]/5 pb-2">
                  <span className="font-semibold text-[#39FF14]">{l.actor_name}</span> <span className="text-[#A1B5A8]">— {l.action} : {l.target}</span>
                  {l.detail && <span className="text-[#697C6F]"> ({l.detail})</span>}
                  <div className="text-[10px] text-[#697C6F]">{new Date(l.created_at).toLocaleString("fr-FR")}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {isAdmin && (
        <div className="rounded-xl border border-[#FF3B30]/40 bg-[#FF3B30]/5 p-5 mt-6" data-testid="danger-zone">
          <h3 className="font-bold flex items-center gap-2 text-[#FF3B30] mb-1"><AlertTriangle size={18} /> Zone dangereuse</h3>
          <p className="text-sm text-[#A1B5A8] mb-4">
            Remettre à zéro « Le Jeu » et le Classement : efface définitivement toutes les courses importées de Geny,
            tous les paris et points des joueurs, ainsi que les classements et saisons. Réservé à l'administrateur.
          </p>
          <button onClick={resetGame} disabled={resetting} data-testid="reset-game-btn"
            className="text-sm font-bold px-4 py-2.5 rounded-lg bg-[#FF3B30] text-white flex items-center gap-2 disabled:opacity-60">
            {resetting ? <Loader2 className="animate-spin" size={16} /> : <RotateCcw size={16} />}
            Remettre à zéro Le Jeu & le Classement
          </button>
        </div>
      )}
    </div>
  );
}
